import { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { FileText, Download, Loader2, CheckCircle, Crown, Mail } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

type Paper = { id: string; title: string; grade: number; subject: string; questionCount: number };

export default function PaperDownloadSuccess({ params }: { params: { token: string } }) {
  const { toast } = useToast();
  const token = params.token;
  const [downloadToken, setDownloadToken] = useState<string | null>(null);
  const [papers, setPapers] = useState<Paper[]>([]);
  const [email, setEmail] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(true);

  // If token looks like a Stripe session ID, verify it first
  useEffect(() => {
    async function verify() {
      if (token.startsWith('cs_')) {
        // Stripe session ID - verify and get download token
        try {
          const res = await fetch('/api/practice-papers/guest-verify', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionId: token }),
          });
          if (!res.ok) throw new Error('Verification failed');
          const data = await res.json();
          setDownloadToken(data.downloadToken);
          setPapers(data.papers || []);
          setEmail(data.email);
          // Update URL to use the download token
          window.history.replaceState({}, '', `/practice-papers/download/${data.downloadToken}`);
        } catch (err) {
          toast({ title: "Verification failed", description: "Please contact support.", variant: "destructive" });
        }
      } else {
        // Already a download token
        setDownloadToken(token);
        try {
          const res = await fetch(`/api/practice-papers/guest-download/${token}`);
          if (!res.ok) throw new Error('Not found');
          const data = await res.json();
          setPapers(data.papers || []);
          setEmail(data.email);
        } catch {
          toast({ title: "Link not found", description: "This download link may be invalid.", variant: "destructive" });
        }
      }
      setVerifying(false);
    }
    verify();
  }, [token]);

  const handleDownload = async (paperId: string) => {
    if (!downloadToken) return;
    try {
      const res = await fetch(`/api/practice-papers/guest-download/${downloadToken}/${paperId}`);
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const paper = papers.find(p => p.id === paperId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${paper?.title?.replace(/\s+/g, '_') || 'Practice_Paper'}.pdf`;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
    } catch {
      toast({ title: "Download failed", variant: "destructive" });
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
          <p className="text-muted-foreground">Verifying your purchase...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-4 py-12">
        {/* Success header */}
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mb-4">
            <CheckCircle className="h-8 w-8 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold mb-1">Your Papers Are Ready!</h1>
          <p className="text-muted-foreground">
            {email && (
              <span className="flex items-center justify-center gap-1.5 text-sm">
                <Mail className="h-3.5 w-3.5" />
                Download links also sent to {email}
              </span>
            )}
          </p>
        </div>

        {/* Paper list */}
        <div className="space-y-3 mb-8">
          {papers.map(paper => (
            <Card key={paper.id}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <FileText className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{paper.title}</p>
                    <p className="text-xs text-muted-foreground">{paper.questionCount} questions</p>
                  </div>
                </div>
                <Button size="sm" onClick={() => handleDownload(paper.id)} className="gap-1.5">
                  <Download className="h-3.5 w-3.5" />
                  Download
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {papers.length === 0 && !verifying && (
          <div className="text-center py-8 text-muted-foreground">
            <p>No papers found for this download link.</p>
          </div>
        )}

        {/* Upsell */}
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="p-6 text-center">
            <Crown className="h-6 w-6 text-primary mx-auto mb-2" />
            <h3 className="font-semibold mb-1">Want unlimited papers?</h3>
            <p className="text-sm text-muted-foreground mb-3">
              Subscribe to EduFocal Academy for unlimited practice papers plus quizzes, progress tracking, and gamified learning.
            </p>
            <Button asChild>
              <a href="/subscribe">View Plans</a>
            </Button>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Bookmark this page. Your download links never expire.
        </p>
      </div>
    </div>
  );
}
